/** Loading, empty and error primitives shared by every screen. */

import type { ReactNode } from 'react';
import { ApiRequestError } from '../lib/api';

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="row" style={{ gap: 'var(--s-3)' }}>
      <span className="spinner" />
      {label ? <span className="muted">{label}</span> : null}
      <span className="sr-only">Loading</span>
    </span>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="empty">
      <Spinner label={label} />
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      {hint ? <div style={{ maxWidth: '46ch' }}>{hint}</div> : null}
      {action}
    </div>
  );
}

/**
 * Error rendering.
 *
 * `detail` is the field that actually helps — the API puts the remedy there
 * (how to work around a bare-IP panel, what the panel said when it rejected the
 * credentials) — so it is always shown when present, never collapsed away.
 */
export function ErrorNotice({ error, action }: { error: unknown; action?: ReactNode }) {
  if (!error) return null;

  const api = error instanceof ApiRequestError ? error : null;
  const message = api ? api.message : error instanceof Error ? error.message : String(error);
  const detail = api?.detail ?? hintFor(api?.code);

  return (
    <div className="notice notice--error" role="alert">
      <div className="notice__title">{message}</div>
      {detail ? <div className="notice__detail">{detail}</div> : null}
      {action ? <div className="row" style={{ marginTop: 'var(--s-4)' }}>{action}</div> : null}
    </div>
  );
}

/**
 * Fallback guidance for the panel error codes, used only when the server did
 * not attach a `detail` of its own.
 */
function hintFor(code: string | undefined): string | null {
  switch (code) {
    case 'panel_ip_literal':
      return 'Cloudflare Workers cannot connect to a bare IP address. Use the hostname your provider gave you, or ask them for one.';
    case 'panel_rejected':
      return 'The panel refused these credentials. Check the username and password, and whether the line is locked to another device.';
    case 'panel_expired':
      return 'This line has expired. Renew it with your provider, then run Check again.';
    case 'panel_unreachable':
      return 'The panel did not answer. It may be down, or blocking Cloudflare egress.';
    default:
      return null;
  }
}
